import { FC, useEffect, useState } from 'react'
import { PageTitle } from '../../../../_metronic/layout/core'
import { ModularDataTable } from '../../../components/ModularDataTable/ModularDataTable'
import type { TableColumn, TablePagination, TableAction } from '../../../components/ModularDataTable/types'
import { Button } from '../../../components/Button/Button'
import { Input } from '../../../components/Input/Input'
import useModals from '../../../hooks/useModals'
import { DELETE, GET, POST, PUT } from '../../../services/api'
import { Loading } from '../../../components/Loading/Loading'
import { ModalDelete } from '../../../components/Modals/General/ModalDelete/ModalDelete'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { BreadCrumbs } from '../../../components/BreadCrumbs/breadCrumbrs'
import { i18n } from '../../../translate/i18n'
import { AgeGroup } from '../Activities/types'
import { dateUSToBr } from '../../../utils/formater'

// Types
export enum ScheduleType {
    FOOD = 'food',
    ACTIVITY = 'activity',
    EVENT = 'event',
    TOUR = 'tour',
    ENTERTAINMENT = 'entertainment',
}

interface HotelSchedule {
    id: number
    pluginspaceId: number
    title: string
    description: string
    location: string
    type: ScheduleType
    ageGroup: AgeGroup
    time: string
    days?: string
    dateInit?: string
    dateEnd?: string
    status: boolean
    createdAt: string
    updatedAt: string
}

interface FilterScheduleDto {
    title?: string
    type?: ScheduleType
    location?: string
    status?: boolean
    dateInit?: string
    dateEnd?: string
    ageGroup?: AgeGroup
}

const SchedulesContent: FC = () => {
    const navigate = useNavigate()
    const { showDeleteModal } = useModals()
    const [loading, setLoading] = useState(false)
    const [schedules, setSchedules] = useState<HotelSchedule[]>([])
    const [pagination, setPagination] = useState<TablePagination>({
        page: 1,
        pageSize: 25,
        total: 0,
        pageSizes: [10, 25, 50, 100]
    })
    const [filters, setFilters] = useState<FilterScheduleDto>({})
    const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null)

    const getSchedules = async (take: number, page: number, searchFilters?: FilterScheduleDto) => {
        try {
            setLoading(true)

            const query = `field=id&page=${page - 1}&take=${take}`

            const filtersToUse = searchFilters || filters
            const res = await POST('/hotel-schedule/filter?' + query, filtersToUse)

            setSchedules(res.data || [])
            setPagination(prev => ({
                ...prev,
                total: res.pagination?.total || 0,
                page: page,
                pageSize: take
            }))
            setLoading(false)
        } catch (e) {
            setLoading(false)
            console.log(e)
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Erro ao carregar as programações!',
            })
        }
    }

    const handleDelete = async () => {
        if (!selectedScheduleId) return

        try {
            await DELETE(`/hotel-schedule/${selectedScheduleId}`)

            Swal.fire({
                icon: 'success',
                title: 'Sucesso',
                text: 'Programação excluída com sucesso!',
            })

            getSchedules(10, 1, filters)
            setSelectedScheduleId(null)
        } catch (e) {
            console.log(e)
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Erro ao excluir a programação!',
            })
        }
    }

    const handleFilterChange = (field: keyof FilterScheduleDto, value: any) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }))
    }

    const handleSearch = () => {
        getSchedules(pagination.pageSize, 1, filters)
    }

    const handleClearFilters = () => {
        setFilters({})
        getSchedules(pagination.pageSize, 1, {})
    }

    const formatScheduleData = (data: HotelSchedule[]) => {
        const newData = data.map(item => ({
            ...item,
            typeLabel: getTypeLabel(item.type),
            ageGroupLabel: getAgeGroupLabel(item.ageGroup),
            daysFormatted: formatDays(item.days),
            dateRangeFormatted: formatDateRange(item.dateInit, item.dateEnd),
        }))
        return newData
    }

    const getTypeLabel = (type: ScheduleType) => {
        const labels = {
            [ScheduleType.ACTIVITY]: 'Atividade',
            [ScheduleType.ENTERTAINMENT]: 'Entretenimento',
            [ScheduleType.FOOD]: 'Gastronomia',
            [ScheduleType.EVENT]: 'Evento',
            [ScheduleType.TOUR]: 'Passeio',

        }
        return labels[type] || type
    }

    const formatDays = (days?: string) => {
        if (!days) return i18n.t('hospitality_schedules.singleEvent')

        const dayNames = [
            i18n.t('shifts.sunday').slice(0, 3),
            i18n.t('shifts.monday').slice(0, 3),
            i18n.t('shifts.tuesday').slice(0, 3),
            i18n.t('shifts.wednesday').slice(0, 3),
            i18n.t('shifts.thursday').slice(0, 3),
            i18n.t('shifts.friday').slice(0, 3),
            i18n.t('shifts.saturday').slice(0, 3),
        ]
        const selectedDays = days.split('').map(day => dayNames[parseInt(day)])
        return selectedDays.join(', ')
    }

    const formatDateRange = (dateInit?: string, dateEnd?: string) => {
        if (!dateInit && !dateEnd) return '-'
        if (dateInit && dateEnd) {
            return `${dateUSToBr(dateInit)} - ${dateUSToBr(dateEnd)}`
        }
        return '-'
    }

    const switchStatus = async (activity: HotelSchedule, status: boolean) => {
        try {
            const switchProduct = await PUT(`/hotel-schedule/${activity.id}`, {
                status: !status
            })
            if (switchProduct.success) {
                const newProducts = schedules.map((product) => {
                    if (product.id === activity.id) {
                        product.status = !status
                    }
                    return product
                })
                setSchedules(newProducts)
            }
        } catch (e) {
            console.log(e)
        }
    }

    useEffect(() => {
        getSchedules(pagination.pageSize, pagination.page, filters)
    }, [pagination.pageSize, pagination.page,])



    // Definição das colunas para o ModularDataTable
    const columns: TableColumn[] = [
        {
            key: i18n.t('hospitality_schedules.table.title'),
            value: 'title',
            type: 'text',
            sortable: true,
        },
        {
            key: i18n.t('hospitality_schedules.table.age_group'),
            value: 'ageGroupLabel',
            type: 'text',
            sortable: true,
        },
        {
            key: i18n.t('hospitality_schedules.table.type'),
            value: 'typeLabel',
            type: 'text',
            sortable: true,
        },
        {
            key: i18n.t('hospitality_schedules.table.location'),
            value: 'location',
            type: 'text',
            sortable: true,
        },
        {
            key: i18n.t('hospitality_schedules.table.time'),
            value: 'time',
            type: 'text',
            sortable: true,
        },
        {
            key: i18n.t('hospitality_schedules.table.recurrence'),
            value: 'daysFormatted',
            type: 'text',
            sortable: false,
        },
        {
            key: i18n.t('hospitality_schedules.table.period'),
            value: 'dateRangeFormatted',
            type: 'text',
            sortable: false,
        },
        {
            key: i18n.t('hospitality_schedules.table.status'),
            value: 'status',
            type: 'custom',
            sortable: true,
            render: (value, row) => (
                <div className="form-check form-switch">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        checked={value}
                        onChange={() => switchStatus(row, value)}
                    />
                </div>
            )
        }
    ]

    // Ações da tabela
    const tableActions: TableAction[] = [
        {
            label: i18n.t('hospitality_schedules.edit_button'),
            icon: 'fas fa-pen',
            color: 'primary',
            action: (item: HotelSchedule) => {
                navigate(`/hospitality/hotel-schedules/editar/${item.id}`)
            }
        },
        {
            label: i18n.t('hospitality_schedules.delete_button'),
            icon: 'fas fa-trash',
            color: 'danger',
            action: (item: HotelSchedule) => {
                setSelectedScheduleId(item.id)
                showDeleteModal(i18n.t('hospitality_schedules.delete_button'))
            }
        }
    ]

    const getAgeGroupLabel = (ageGroup: AgeGroup): string => {
        const ageGroupLabels = {
            [AgeGroup.CHILDREN]: i18n.t('hospitality_schedules.age_groups.children'),
            [AgeGroup.ADULTS]: i18n.t('hospitality_schedules.age_groups.adults'),
            [AgeGroup.FAMILY]: i18n.t('hospitality_schedules.age_groups.family'),
            [AgeGroup.TEENS]: i18n.t('hospitality_schedules.age_groups.teens'),
            [AgeGroup.SENIORS]: i18n.t('hospitality_schedules.age_groups.seniors'),
        }
        return ageGroupLabels[ageGroup] || ageGroup
    }

    const optionsAgeGroup: any[] = []
    optionsAgeGroup.push({ value: '', select: i18n.t('hospitality_schedules.age_groups.all') })
    Object.values(AgeGroup).map((ageGroup) => (
        optionsAgeGroup.push({ value: ageGroup, select: getAgeGroupLabel(ageGroup) })
    ))

    const handleDataChange = async (params: any) => {
        const { page, pageSize, search, sorting } = params

        // Atualiza filtros se houver busca
        let newFilters = { ...filters }
        if (search) {
            newFilters = { ...newFilters, title: search }
        } else {
            delete newFilters.title
        }

        setFilters(newFilters)
        await getSchedules(pageSize, page, newFilters)
    }


    return (
        <>
            <div className='row g-5 gx-xxl-12'>
                <div className='card card-xxl-stretch mb-5 mb-xl-12'>
                    <div className='card-header py-5'>
                        <h3 className='card-title align-items-start flex-column'>
                            <span className='card-label fw-bolder fs-3 mb-1'>
                                {i18n.t('hospitality_schedules.page_title')}
                            </span>
                            <BreadCrumbs />
                            <span className='text-muted mt-1 fw-bold fs-7'>
                                {i18n.t('hospitality_schedules.page_subtitle')}
                            </span>
                        </h3>
                        <div className='card-toolbar'>
                            <Button
                                text={i18n.t('hospitality_schedules.new_button')}
                                color='primary'
                                icon='fas fa-plus'
                                size='small'
                                click={() => navigate('/hospitality/hotel-schedules/criar')}
                            />
                        </div>
                    </div>
                    <div className='card-body border-bottom'>
                        <div className='row g-3'>


                            <div className='col-md-2'>
                                <Input
                                    label={i18n.t('hospitality_schedules.filters.type')}
                                    inputAttr={{
                                        type: 'select',
                                        placeholder: i18n.t('hospitality_schedules.filters.type_placeholder'),
                                    }}
                                    value={filters.type || ''}
                                    change={(value: ScheduleType) => handleFilterChange('type', value || undefined)}
                                    options={[
                                        { value: '', select: i18n.t('hospitality_schedules.table.all_types') },
                                        { value: ScheduleType.ACTIVITY, select: i18n.t('hospitality_schedules.types.activity') },
                                        { value: ScheduleType.FOOD, select: i18n.t('hospitality_schedules.types.food') },
                                        { value: ScheduleType.EVENT, select: i18n.t('hospitality_schedules.types.event') },
                                        { value: ScheduleType.TOUR, select: i18n.t('hospitality_schedules.types.tour') },
                                    ]}
                                />
                            </div>
                            <div className='col-md-2'>
                                <Input
                                    inputAttr={{
                                        type: 'select',
                                    }}
                                    label={i18n.t('hospitality_activities.field_age_group')}
                                    options={optionsAgeGroup}
                                    value={filters.ageGroup}
                                    change={(value: AgeGroup) => handleFilterChange('ageGroup', value)}
                                />

                            </div>
                            <div className='col-md-2'>
                                <Input
                                    label={i18n.t('hospitality_schedules.filters.status')}
                                    inputAttr={{
                                        type: 'select',
                                        placeholder: i18n.t('hospitality_schedules.filters.status_placeholder'),
                                    }}
                                    value={filters.status !== undefined ? filters.status.toString() : ''}
                                    change={(value: string) => handleFilterChange('status', value === '' ? undefined : value === 'true')}
                                    options={[
                                        { value: '', select: i18n.t('hospitality_schedules.table.all_status') },
                                        { value: 'true', select: i18n.t('hospitality_schedules.table.active') },
                                        { value: 'false', select: i18n.t('hospitality_schedules.table.inactive') },
                                    ]}
                                />
                            </div>


                            <div className='col-md-3'>
                                <Input
                                    label={i18n.t('hospitality_schedules.filters.date_init')}
                                    inputAttr={{
                                        type: 'date',
                                        placeholder: i18n.t('hospitality_schedules.filters.date_init_placeholder'),
                                    }}
                                    value={filters.dateInit || ''}
                                    change={(value: string) => handleFilterChange('dateInit', value || undefined)}
                                />
                            </div>

                            <div className='col-md-3'>
                                <Input
                                    label={i18n.t('hospitality_schedules.filters.date_end')}
                                    inputAttr={{
                                        type: 'date',
                                        placeholder: i18n.t('hospitality_schedules.filters.date_end_placeholder'),
                                    }}
                                    value={filters.dateEnd || ''}
                                    change={(value: string) => handleFilterChange('dateEnd', value || undefined)}
                                />
                            </div>

                            <div className='col-12'>
                                <div className='d-flex gap-2'>
                                    <Button
                                        text={i18n.t('hospitality_schedules.filters.search')}
                                        color='primary'
                                        size='small'
                                        click={handleSearch}
                                    />
                                    <Button
                                        text={i18n.t('hospitality_schedules.filters.clear')}
                                        color='secondary'
                                        size='small'
                                        click={handleClearFilters}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='card-body'>
                        <ModularDataTable
                            data={formatScheduleData(schedules)}
                            columns={columns}
                            actions={tableActions}
                            pagination={pagination}
                            onPaginationChange={setPagination}
                            onDataChange={handleDataChange}
                            loading={loading}
                            searchable={true}
                            searchPlaceholder={i18n.t('hospitality_schedules.filters.search')}
                            striped={false}
                            hover={true}
                            size="md"
                        />
                    </div>
                </div>


                <ModalDelete
                    text={i18n.t('hospitality_schedules.delete_confirm')}
                    confirmAction={handleDelete}
                />
            </div>
        </>
    )
}

const SchedulesIndex: FC = () => {
    return (
        <>
            <PageTitle
                breadcrumbs={[
                    { title: 'Dashboard', path: 'dashboard', isActive: true },
                    { title: i18n.t('hospitality_schedules.page_title'), path: 'hospitality', isActive: true },
                    { title: i18n.t('hospitality_schedules.page_title'), path: 'hospitality/schedules', isActive: true },
                ]}
            >
                {i18n.t('hospitality_schedules.page_title')}
            </PageTitle>
            <SchedulesContent />
        </>
    )
}

export { SchedulesIndex }
